import { GetShowResponseShow, Shows, ShowsAudiencePage, ShowsSimplified, SingleShow, SingleShowSimplified } from "./Show";
import { UserInterface, UserListInterface } from "./User";
import { SingleSessionDashboard } from "./SessionDashboard";
import { ShowDashboard, ShowsDashboard } from "./ShowDashboard";
import { Sessions, SessionsAggregated, SingleSession } from "./Session";
import { Allocation, AllocationDetails, Allocations } from "./Allocation";
import { IBookingTicket } from "./BookingTicket";
import { AllSessionDetailsInterface, SessionDetailInterface } from "./SessionDetails";
import { BookingInterestInfo, UserBookingInterest, UserBookingInterestsInterface, BookingInterestAccessibilityRequestInterface, BookingInterestsAccessibilityRequestInterface, BookingInterestsMinAgeOverrideRequestInterface } from "./BookingInterest";
import { Ticket } from "./Ticket";
import { Templates } from "./Template";
import { BookingInterestStatus } from "../../enums/BookingInterestStatus";
import { PriorityAllocationCheckResponse, ShowDetails, UpdateAllocationResult } from "./AllocationV2";
import { ConfirmEmailData } from "./registration/confirm-email-data";
import { User as UserForTickets } from "./ticketingDashboard/User";
import { Show as AudienceShow } from "./bookingManager/Show";
import { ProblemDetails } from "./ProblemDetails";
import { Show as AudiencePageShow } from "./audience/Show";
import { LoginResponse } from "./Login";
import { RegisterResponse } from "./Register";

export interface Show {
  id: number;
  firstName: string | null;
  lastName: string | null;
  phoneNumber: string | null;
  numberOfTickets: number | null;
  email: string | null;
}

export interface User {
  id: number;
  name: string | null;
  permissionLvl: string | null;
}

export interface PageInformation {
  totalCount: number;
  pageSize: number;
  pageIndex: number;
}

export interface FiltersInformation<T> {
  filters?: T | null;
}

interface ApiResponseConstructorParams<T> {
  data?: T;
  pageInformation?: PageInformation;
  error?: string | ProblemDetails;
}

interface ApiFiltersResponseConstructorParams<T, F> {
  data?: T;
  pageInformation?: PageInformation;
  filters?: F;
  error?: string;
}

export abstract class ApiResponse<T> {
  data: T | null;
  pageInformation: PageInformation | null;
  error: string | null | ProblemDetails;

  constructor({
    data,
    pageInformation,
    error,
  }: ApiResponseConstructorParams<T>) {
    this.data = data || null;
    this.pageInformation = pageInformation || null;
    this.error = error || null;
  }
}

export abstract class ApiFiltersResponse<T, F> {
  data: T | null;
  pageInformation: PageInformation | null;
  filters: F | null;
  error: string | null;
  constructor({
    data,
    pageInformation,
    filters,
    error,
  }: ApiFiltersResponseConstructorParams<T, F>) {
    this.data = data || null;
    this.pageInformation = pageInformation || null;
    this.filters = filters || null;
    this.error = error || null;
  }
}

export type entityId = number;
export interface IBookingInterestUserInfo extends IBookingTicket { }

export class SessionDashboardResponse extends ApiResponse<SingleSessionDashboard> { }
export class ShowDashboardResponse extends ApiResponse<ShowDashboard> { }
export class ShowsDashboardResponse extends ApiResponse<ShowsDashboard> { }
export class GetShowResponse extends ApiResponse<GetShowResponseShow> { }
export class ShowResponse extends ApiResponse<SingleShow> { }
export class ShowV2Response extends ApiResponse<ShowDetails> { }
export class ShowsResponse extends ApiResponse<Shows> { }
export class ShowsAudiencePageResponse extends ApiResponse<ShowsAudiencePage> { }
export class AudiencePageResponse extends ApiResponse<AudiencePageShow[]> {}
export class ShowResponseSimplified extends ApiResponse<SingleShowSimplified> { }
export class ShowsResponseSimplified extends ApiResponse<ShowsSimplified> { }
export class SessionResponse extends ApiResponse<SingleSession> { }
export class SessionsResponse extends ApiResponse<Sessions> { }
export class AudianceSessionsResponse extends ApiResponse<AudienceShow> {}
export class IsPriorityAllocationFullResponse extends ApiResponse<PriorityAllocationCheckResponse> {}
export class SessionsAggregatedResponse extends ApiResponse<SessionsAggregated> { }
export class SessionDetailsResponse extends ApiResponse<SessionDetailInterface> { }
export class AllSessionDetailsResponse extends ApiResponse<AllSessionDetailsInterface> { }
export class UserResponse extends ApiFiltersResponse<UserInterface, UserFilters> { }
export class UserListResponse extends ApiFiltersResponse<UserListInterface, UserFilters> { }
export class BookingInterestInfoResponse extends ApiResponse<BookingInterestInfo> { }
export class BarcodeResponse extends ApiResponse<ScanResult> { }
export class TurnedAwayResponse extends ApiResponse<TurnedAwayResult> { }
export class AccessGivenResponse extends ApiResponse<AccessGivenResult> { }
export class CreateBookingInterestResponse extends ApiResponse<CreateEntityResult> { }
export class BulkCsvAddBookingInterestsResponse extends ApiResponse<BulkAddCommandResult> { }
export class TicketResponse extends ApiResponse<Ticket> { }

export class BookingInterestAccessibilityRequestResponse extends ApiResponse<BookingInterestAccessibilityRequestInterface> { }
export class BookingInterestsAccessibilityRequestResponse extends ApiResponse<BookingInterestsAccessibilityRequestInterface> { }
export class BookingInterestsMinAgeOverrideResponse extends ApiResponse<BookingInterestsMinAgeOverrideRequestInterface> {}
export class UserBookingInterestResponse extends ApiResponse<UserBookingInterest> { }
export class UserBookingInterestsResponse extends ApiResponse<UserBookingInterestsInterface> { }
export class TemplatesResponse extends ApiResponse<Templates> { }
export class AllocationResponse extends ApiResponse<Allocation> { }
export class AllocationsResponse extends ApiResponse<Allocations> { }
export class AllocationDetailsResponse extends ApiResponse<AllocationDetails> { }
export class MyAllocationsResponse extends ApiResponse<MyAllocation[]> { }
export class RevokeBookingInterestResponse extends ApiResponse<boolean> {}
export class UpdateBookingInterestResponse extends ApiResponse<boolean> {}
export class BulkSendBookingInterestResponse extends ApiResponse<boolean> {}
export class UpdateAllocationResponse extends ApiResponse<UpdateAllocationResult[]> { }
export class ConfirmEmailResponse extends ApiResponse<ConfirmEmailData> {}
export class UserForTicketsResponse extends ApiResponse<UserForTickets[]>{}
export class LoginApiResponse extends ApiResponse<LoginResponse>{}
export class RegisterApiResponse extends ApiResponse<RegisterResponse>{}
export class MinAgeOverrideResponse extends ApiResponse<boolean>{}

export type ScanStatistics = {
  name: string;
  value: string
};

export type ScanInfo = {
  name: string;
  barcode?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  isDependent?: boolean;
  dependentOf?: {
    firstName?: string;
    lastName?: string;
  },
  ticket?: {
    type?: string;
  },
  status?: string;
  turnAwayReason?: string;
  isLeadBooker?: boolean,
  isUnderage?: boolean
};

export type GroupTicketScan = {
  isGroupTicket: boolean;
  numberOfGuests: number;
};

export type ScanLog = {
  barcode: string;
  name: string;
  result: string;
};

export type ScanResult = {
  barcodeScanErrorType: string | null;
  scanInfo: ScanInfo | null;
  scanStatistics: ScanStatistics[];
  isGroupTicket: boolean;
  numberOfGuests: number;
};

export type TurnedAwayResult = {
  barCode: string,
  name: string,
  result: string,
  error: string
}

export type AccessGivenResult = {
  barCode: string,
  name: string,
  result: string,
  error: string
}

export type CreateEntityResult = {
  bookingInterestId: number | null;
  bookingInterestStatus: keyof typeof BookingInterestStatus | undefined | null;
};

export interface BulkAddCommandResult {
  results?: (CreateResult)[] | null;
  validationErrors?: (ValidationErrorsEntity)[] | null;
}
export interface CreateResult {
  lineNumber: number;
  result: CreateBookingInterestResult;
}
export interface CreateBookingInterestResult {
  bookingInterestId: number | null;
  bookingInterestStatus: keyof typeof BookingInterestStatus | undefined | null;
  error: string | null;
}
export interface ValidationErrorsEntity {
  errors?: (string)[] | null;
  lineNumber: number;
}

export type UserFilters = {
  name: string | null;
  creationDateFrom: string | null;
  creationDateTo: string | null;
  email: string | null;
  ageFrom: number | null;
  ageTo: number | null;
  address: string | null;
  showId: number[] | null;
  sessionId: number[] | null;
  bookingInterestStatus: string | null;
  [key: string]: string | number | number[] | null;
}

export const BarcodeScanErrorType = {
  WRONG_SESSION: "WrongSession",
  TICKET_NOT_FOUND: "TicketNotFound",
  ALREADY_USED: "AlreadyUsed",
  CANCELLED: "Cancelled",
  DECLINED_BY_THE_TICKET_HOLDER: "DeclinedByTheTicketHolder",
  NOT_CONFIRMED: "NotConfirmed",
  TURNED_AWAY: "TurnedAway",
};

export const SignUpStatusType = {
  INVITED_STUB_USER: "InvitedStubUser",
  INCOMPLETE_REGISTRATION: "IncompleteRegistration",
  COMPLETE_REGISTRATION: "CompleteRegistration",
  DEPENDENT_USER: "DependentUser",
};

export const parseMessageFromBarcodeScanErrorType = (
  barcodeScanErrorType: string | null,
) => {
  let message: string = "";
  switch (barcodeScanErrorType) {
    // Removed for now, see TTT-733 (might be turned on later on)
    /* case BarcodeScanErrorType.ALREADY_USED:
      message = "The barcode has been used already";
      break; */
    case BarcodeScanErrorType.CANCELLED:
      message = "Cancelled";
      break;
    case BarcodeScanErrorType.DECLINED_BY_THE_TICKET_HOLDER:
      message = "Declined by the ticketholder";
      break;
    case BarcodeScanErrorType.TICKET_NOT_FOUND:
      message = "Ticket not found";
      break;
    case BarcodeScanErrorType.WRONG_SESSION:
      message = "Wrong session";
      break;
    case BarcodeScanErrorType.NOT_CONFIRMED:
      message = "SMS Not Confirmed";
      break;
    case BarcodeScanErrorType.TURNED_AWAY:
      message = "Turned Away";
      break;
    default:
      if (barcodeScanErrorType) {
        message = barcodeScanErrorType;
      }
      break;
  }
  return message;
};

export const parseMessageSignUpStatus = (
  signUpStatus: string | null,
) => {
  let message = "";
  switch (signUpStatus) {
    case SignUpStatusType.DEPENDENT_USER:
      message = "Dependent User";
      break;
    case SignUpStatusType.INVITED_STUB_USER:
      message = "Invited Stub User";
      break;
    case SignUpStatusType.INCOMPLETE_REGISTRATION:
      message = "Incomplete Registration";
      break;
    case SignUpStatusType.COMPLETE_REGISTRATION:
      message = "Complete Registration";
      break;
    default:
      break;
  }
  return message;
};

export interface MyAllocation {
  id: number;
  name: string;
  parentAllocationId: number | null;
  publicRouteUrl: string;
  imageUrl: string;
  sessionsCount: number;
  nextSessionDeadline: Date;
  nextSessionDeadlineOffSet: number;
  showId: number;
  showName: string;
  subAllocationsCount: number;
  allocatedTicketsCount: number;
  pendingBookingInterests: number;
  acceptedBookingInterests: number;
  declinedBookingInterests: number;
}